import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useState, useEffect } from "react";
import SEO from "@/components/SEO";
import { buildBreadcrumbs } from "@/lib/structured-data";
import { 
  Shield, 
  FileText, 
  Waves, 
  TreePine, 
  CheckCircle, 
  Phone, 
  Mail, 
  Award,
  Monitor,
  Clock,
  Users,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { Link } from "react-router-dom";

export default function ServicesPage() {
  const [expandedCard, setExpandedCard] = useState<number | null>(null);

  // Scroll animation effect
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate');
        }
      });
    }, observerOptions);

    // Observe all elements with scroll-animate class
    const animateElements = document.querySelectorAll('.scroll-animate');
    animateElements.forEach((el) => observer.observe(el));

    return () => {
      animateElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  const mainServices = [
    {
      icon: FileText,
      title: "Local Authority Searches",
      description: "Comprehensive regulated searches, search code compliant accepted by 99.9 % of Lenders and Official Local Authority Searches",
      shortFeatures: ["National Coverage", "5 days for most searches", "Regulated Searches"],
      borderColor: "border-wl-blue",
      iconColor: "text-wl-blue",
      iconBg: "bg-wl-blue/10",
      detailedServices: [
        { name: "Official LLC1 & CON29", description: "Standard local authority searches for England & Wales" },
        { name: "Personal Searches", description: "Hand-delivered searches for urgent requirements" },
        { name: "Official Copy Searches", description: "Land Registry official copies and title documents" },
        { name: "Council Documents", description: "Copies of planning and building control documents" }
      ]
    },
    {
      icon: TreePine,
      title: "Environmental Searches",
      description: "Comprehensive environmental risk assessments and contamination reports.",
      shortFeatures: ["Ground Stability", "Contamination Risk", "Historical Land Use"],
      borderColor: "border-wl-green",
      iconColor: "text-wl-green",
      iconBg: "bg-wl-green/10",
      detailedServices: [
        { name: "Landmark Environmental", description: "Database searches and historical land use reports" },
        { name: "Groundsure Reports", description: "Comprehensive environmental and geo-hazard assessments" },
        { name: "Martello Reports", description: "Martello reports for all property types" },
        { name: "Independant Utility Searches", description: "Search for all utilities including water, electricity, gas, and telecoms" }
      ]
    },
    {
      icon: Waves,
      title: "Water & Drainage",
      description: "Complete water authority and drainage reports for all property types.",
      shortFeatures: ["Official CON29DW", "Regulated Drainage Searches", "Infrastructure Details"],
      borderColor: "border-wl-navy",
      iconColor: "text-wl-navy",
      iconBg: "bg-wl-navy/10",
      detailedServices: [
        { name: "National Coverage", description: "Official coverage of England and Wales" }
      ]
    },
    {
      icon: Shield,
      title: "Compliance & Risk",
      description: "AML reports, lawyer checks, and comprehensive compliance services.",
      shortFeatures: ["AML Reports", "Source of Funds"],
      borderColor: "border-wl-blue",
      iconColor: "text-wl-blue",
      iconBg: "bg-wl-blue/10",
      detailedServices: [
        { name: "AML Reports", description: "Anti-money laundering checks, Internation AML and compliance reporting" },
        { name: "Windoing Up Searches", description: "Windoing up searches for insolvency and bankruptcy" },
        { name: "Company Searches", description: "Companies House searches and director information" }
      ]
    },
    {
      icon: Monitor,
      title: "Technology Solutions",
      description: "Modern platforms and systems to streamline your workflow.",
      shortFeatures: ["Online Ordering", "Real-time Updates", "Case Management"],
      borderColor: "border-wl-green",
      iconColor: "text-wl-green",
      iconBg: "bg-wl-green/10",
      detailedServices: [
        { name: "Online Ordering Platform", description: "Streamlined digital ordering and case management" },
        { name: "API Integration", description: "Connect our services to your existing systems" },
        { name: "Automated Reporting", description: "Real-time status updates and delivery notifications" },
        { name: "Document Management", description: "Secure storage and retrieval of all search documents" }
      ]
    },
    {
      icon: Award,
      title: "Insurance & Protection",
      description: "Comprehensive insurance solutions for property transactions.",
      shortFeatures: ["Indemnity Insurance", "Risk Protection", "Abortive Transaction Insurance"],
      borderColor: "border-wl-navy",
      iconColor: "text-wl-navy",
      iconBg: "bg-wl-navy/10",
      detailedServices: [
        { name: "Title and Legal Indemnity Insurance", description: "Protection when searches cannot be obtained" },
        { name: "No Search chancel Insurance", description: "Coverage for unexpected chancel repair liabilities" }
      ]
    }
  ];

  const toggleCard = (index: number) => {
    setExpandedCard(expandedCard === index ? null : index);
  };

  return (
    <div className="min-h-screen pt-28">
      <SEO
        title="Our Services - Complete Property Search Solutions"
        description="Comprehensive property search solutions including local authority searches, environmental reports, water & drainage, compliance services, and more."
        keywords="property search services, local authority searches, environmental searches, water drainage searches, conveyancing compliance"
        canonicalUrl="/services"
        structuredData={buildBreadcrumbs([{ name: "Home", path: "/" }, { name: "Services" }])}
      />
      <Navbar />

      {/* Hero Section with Video Backdrop */}
      <section className="relative py-32 bg-wl-navy overflow-hidden">
        {/* Video Background */}
        <video 
          autoPlay 
          loop 
          muted 
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/services-back.mp4" type="video/mp4" />
          {/* Fallback for browsers that don't support video */}
          <div 
            className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: 'url(/city-map.jpg)' }}
          ></div>
        </video>
        
        {/* Single overlay for darkening */}
        <div className="absolute inset-0 bg-wl-navy/80"></div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white/20">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Our Services
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
              Comprehensive property search solutions tailored for auction houses and conveyancing professionals across the UK.
            </p>
          </div>
        </div>
      </section>

      {/* Interactive Service Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 scroll-animate">
            <h2 className="text-3xl md:text-4xl font-bold text-wl-navy mb-6">Complete Property Search Solutions</h2>
            <p className="text-xl text-wl-gray max-w-3xl mx-auto">
              Click on any service below to explore our detailed offerings. We provide everything you need for successful property transactions.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 scroll-animate">
            {mainServices.map((service, index) => (
              <div 
                key={index} 
                className={`service-card bg-white rounded-xl shadow-sm transition-all duration-300 cursor-pointer border-l-4 ${service.borderColor} ${
                  expandedCard === index 
                    ? 'expanded shadow-lg' 
                    : 'hover:shadow-md'
                }`}
                onClick={() => toggleCard(index)}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className={`w-12 h-12 ${service.iconBg} rounded-xl flex items-center justify-center mr-4`}>
                        <service.icon className={`w-6 h-6 ${service.iconColor}`} />
                      </div>
                      <h3 className="text-lg font-bold text-wl-navy">{service.title}</h3>
                    </div>
                    {expandedCard === index ? (
                      <ChevronUp className="w-5 h-5 text-wl-green" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-wl-gray" />
                    )}
                  </div>
                  
                  <p className="text-wl-gray text-sm mb-4">{service.description}</p>
                  
                  {/* Short features - always visible */}
                  <div className="space-y-2 mb-4">
                    {service.shortFeatures.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center text-sm">
                        <CheckCircle className="w-4 h-4 text-wl-green mr-2 flex-shrink-0" />
                        <span className="text-wl-gray">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Expanded content */}
                  {expandedCard === index && (
                    <div 
                      className="border-t pt-4 mt-4 opacity-100 transition-opacity duration-300"
                      style={{ 
                        animation: 'fadeInUp 0.3s ease-out',
                        position: 'relative',
                        zIndex: 1
                      }}
                    >
                      <h4 className="font-semibold text-wl-navy mb-3">Detailed Services:</h4>
                      <div className="space-y-3">
                        {service.detailedServices.map((detailService, detailIndex) => (
                          <div key={detailIndex} className="border-l-3 border-wl-green pl-3">
                            <h5 className="font-medium text-wl-navy text-sm">{detailService.name}</h5>
                            <p className="text-wl-gray text-xs">{detailService.description}</p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 pt-3 border-t">
                        <Button size="sm" className="w-full bg-wl-green hover:bg-wl-green/90">
                          Get Quote for {service.title}
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Click prompt */}
                  {expandedCard !== index && (
                    <div className="text-center">
                      <span className="text-xs text-wl-gray">Click to see detailed services</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Our Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 scroll-animate">
            <h2 className="text-3xl md:text-4xl font-bold text-wl-navy mb-6">Why Choose Our Services?</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center scroll-animate">
              <div className="w-16 h-16 bg-wl-blue/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-wl-blue" />
              </div>
              <h3 className="text-xl font-bold text-wl-navy mb-3">Speed & Reliability</h3>
              <p className="text-wl-gray">
                <span className="font-semibold text-wl-green">7-10 working days guaranteed</span> vs industry average of 142+ days. 
                Same-day urgent service available when you need it most.
              </p>
            </div>
            
            <div className="text-center scroll-animate">
              <div className="w-16 h-16 bg-wl-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-wl-green" />
              </div>
              <h3 className="text-xl font-bold text-wl-navy mb-3">Personal Service</h3>
              <p className="text-wl-gray">
                Real people you can actually speak to. No call centers, no automated systems - just professional service from experts.
              </p>
            </div>
            
            <div className="text-center scroll-animate">
              <div className="w-16 h-16 bg-wl-navy/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-wl-navy" />
              </div>
              <h3 className="text-xl font-bold text-wl-navy mb-3">Fully Compliant</h3>
              <p className="text-wl-gray">
                £2M professional indemnity insurance, PCCB registered, and fully compliant with all industry regulations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-wl-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Contact us today to discuss your requirements and discover how we can streamline your property search needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:01923620240" 
              className="inline-flex items-center justify-center bg-wl-green hover:bg-wl-green/90 text-white px-8 py-4 rounded-lg font-semibold transition-colors"
            >
              <Phone className="w-5 h-5 mr-2" />
              Call 01923 620240/41
            </a>
            <Link 
              to="/contact" 
              className="inline-flex items-center justify-center bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold transition-colors border border-white/20"
            >
              <Mail className="w-5 h-5 mr-2" />
              Email Us Today
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}